import { create } from 'zustand';

// D16 — a plain module function ("Wrapper for ToastQueue... we still need to
// expose global toast state for some non-React consumers", per that file's
// own doc), not a React hook: safe to call from this pure store the same way
// `use-usage-sidebar-decision.ts` calls `addErrorToast` from inside a
// promise continuation rather than component render. The root barrel, not
// the deep `@clay-run/glaze/toast/toast-queue` entry — this file (unlike
// `components/**`) is covered by `tsconfig.tests.json`, whose
// `moduleResolution` can't resolve Glaze's deep entries (see
// `subtype-badge-colors.ts`'s module doc for the same constraint).
import { addToast } from '@clay-run/glaze';

import { closeGenerateSession } from './generate-with-ai-store';
import type { GeneratedCopyReview } from './generated-copy-store';
import type {
  PlaybooksGenerationInputs,
  PlaybooksTemplatePlaybook,
  PlaybooksUpdatePlaysRequest,
  PlaybooksUsagePlace,
  PlaybooksUsagePlay,
  PlaybooksUsageResponse,
  SegmentationConditions,
  SequenceSteps,
} from './schemas';
import { distinctUnsentPlays } from './usage-gate';

/**
 * Launcher V2 U1a — the persistent "Where is this used" sidebar's save/delete
 * gate. Replaces the `openAlertDialog` + `pendingGateRejectRef` +
 * `onCloseOverride` + `choiceMade` shape in `SequencesStep.tsx:608,871-1055`
 * and `SequenceCopyPane.tsx:190-420` (see coding-standards.md §5): that shape
 * resolves/rejects a Promise on a dialog choice, which cannot survive the
 * unmount `SequencesStep` inflicts on its editor tree
 * (`<SequenceEditorShell key={selectedRung.draftKey}>`) or a route change.
 * This store is state, not an awaited dialog (KTD 2) — `openUsageSidebar`
 * never returns a Promise.
 *
 * Module-scope zustand (root CLAUDE.md "Zustand for Module-Scope Client
 * State"), modelled on `sequence-copy-draft-store.ts` / `drawer-store.ts`:
 * plain `create<State>(…)`, exported {@link initialState} so tests reset with
 * `useUsageSidebarStore.setState(initialState, true)`, and actions as
 * module-level functions rather than fields on the state object.
 *
 * Two origins:
 *  - `'play'` — the save/delete was triggered from inside a Play's editor
 *    (`SequencesStep`/`QualifyStep`). Carries `rungDraftKey` (the editing
 *    surface's own identity, `playbook-draft.ts`'s `rungDraftKey()`/`new:N`)
 *    because `rungId` is `null` for a rung that hasn't reached the server
 *    yet — `rungDraftKey` is what stays stable and unique across that.
 *  - `'library'` — triggered from the Config/Library page
 *    (`PlaybooksConfigPage.tsx`), which has no rung of its own.
 *
 * `draftSteps`/`headVersionId` ride along in the store (rather than being
 * re-read at decision time) because a rep's typed copy does NOT survive a
 * route change to another Play — `PlayFlowShell`'s `[playId]` effect calls
 * `clearSequenceCopyDrafts()` in both setup and cleanup — so this store is
 * the only place that copy can still be read from once the sidebar has
 * carried the rep across a navigation (KTD 1).
 */

/**
 * Plan 2026-09-28-006 U3b (rulebook R6, R18) — the identity being saved or
 * deleted, plus the two facts {@link saveMenuOptions} (`usage-sidebar-logic.ts`)
 * needs to decide the Save menu's shape without a role/context argument:
 *
 * `owningPlayId` — `null` for a Library identity; the owning play's id for a
 * copy or a play-created original. `saveMenuOptions` reads this to pick the
 * owned-target ② label ("Update N groups in this play") over the Library-
 * target ③ visibility check.
 *
 * `archivedInLibrary` — [R18] true only for a Library piece an admin
 * archived that this play's ladder still references (`isArchivedInLibrary`,
 * `play-ownership.ts`). ③ is hidden for it — there is no live Library row
 * left to point a playbook at.
 */
export interface UsageSidebarTarget {
  kind: 'sequence' | 'segmentation';
  id: string;
  name: string;
  owningPlayId: string | null;
  archivedInLibrary: boolean;
}

/**
 * Plan 2026-09-28-006 U3b (rulebook R6, R9, R12) — the settings-edit
 * counterpart of `draftSteps`/`draftConditions`, replacing the sender-only
 * `draftSender` (round-6 plan): sender, CC/reply-to, flow id and rename are
 * all "a pending write against a sequence identity" the same way a sender
 * save was, and now share one gated shape instead of sender getting its own
 * bespoke pending field and the other three getting none at all (the R5
 * defect this whole plan fixes). A discriminated union over `field`, one
 * variant per settings edit `saveMenuOptions`'s ②/③ can fan out — passed
 * straight through as the fan-out request body (`SequencesStep.tsx`, plan
 * 2026-09-28-007 U10: deleted the identity-mapping `settingsFanOutChange`
 * helper) — deliberately the SAME
 * shape as `PlaybooksUpdatePlaysRequest['change']`'s settings variants (never
 * a hand-written parallel type — root CLAUDE.md "Schema-First Data
 * Boundaries"), narrowed to the four fields a settings pending can carry
 * (never `'conditions'`/`'steps'`/`'rebind'`, which ride on
 * `draftConditions`/`draftSteps` instead).
 */
export type SequenceSettingsDraft = Extract<
  PlaybooksUpdatePlaysRequest['change'],
  { field: 'sender' | 'inflection' | 'flow' | 'name' }
>;

/**
 * Where the save/delete was triggered from.
 *
 * `rungId: null` is a rung staged this session with no server id yet
 * (`playbook-draft.ts`'s `new:<n>` staging key) — `rungDraftKey` is the
 * stable identity across that, `rungId` becomes non-null once the rung is
 * saved.
 */
export type UsageSidebarOrigin =
  | {
      kind: 'play';
      playId: string;
      campaignSfdcId: string;
      playbookId: string;
      rungId: string | null;
      rungDraftKey: string;
    }
  | {
      // terra-qyt.13 (U9) — the playbook-only-authoring doorway to the SAME
      // editing surface a `'play'` origin describes: no `playId`/
      // `campaignSfdcId` (a playbook can be authored before a campaign is
      // selected), `seedCampaignSfdcId` in their place. `originKeyOf` and
      // `originPlaceKey` (`usage-sidebar-logic.ts`) both treat this and
      // `'play'` identically — one place, two doorways — see their own docs.
      kind: 'playbook';
      playbookId: string;
      seedCampaignSfdcId: string | null;
      rungId: string | null;
      rungDraftKey: string;
    }
  | {
      kind: 'library';
      targetKind: 'sequence' | 'segmentation';
      id: string;
    };

/**
 * What a rep chose in the sidebar, or `null` before they choose.
 * `'resume-review'` (Codex fixes 2026-09-17, F2) — "Keep reviewing instead" /
 * the X on an AI-Apply save: cancel nothing, write nothing, hand the rep back
 * to the Generate drawer at the review stage. Legal in `'save'` mode only
 * (`chooseUsageSidebarAction`'s existing rule — anything but `'delete'`),
 * offered by `UsageSidebar.tsx` only when `generateSessionForPending` is
 * non-null, and consumed at the ORIGIN like every other decision — see
 * `usage-sidebar-resume.ts`.
 *
 * QA round 8, U7 (2026-09-22) — the round-7 `'resume-edit'` value is DELETED.
 * A usage card now opens in a new tab (`UsageSidebar#handleNavigate`), so the
 * page underneath never moves and the excursion that decision existed to
 * recover from cannot happen from this sidebar. A hand-edit X is a plain
 * close again; MANUAL navigation away from unsaved work is
 * `UnsavedWorkGuard`'s job, as it always was.
 *
 * Plan 2026-09-28-006 U3b (rulebook R6, R8) — `'update-plays'` is the fifth
 * decision, option ② of the Save menu ("Update N plays" / "Update N groups in
 * this play"): every unsent play still sharing a Library piece gets its own
 * copy, through `POST /plays/:id/update-plays` (`mutations.ts#updatePlays`).
 * Legal in `'save'` mode only, same as `'update-all'`/`'save-new'`.
 */
export type UsageSidebarDecision =
  | 'update-all'
  | 'save-new'
  | 'update-plays'
  | 'delete'
  | 'resume-review'
  | null;

export interface PendingUsageSidebar {
  mode: 'save' | 'delete';
  target: UsageSidebarTarget;
  origin: UsageSidebarOrigin;
  /** `null` in delete mode — there is nothing to save. Also `null` for a SEGMENTATION save, whose draft rides on {@link PendingUsageSidebar.draftConditions} instead. */
  draftSteps: SequenceSteps | null;
  /**
   * Playbooks QA round 4, U6 (S2) / Decision 13 — the SEGMENTATION
   * counterpart of `draftSteps`, riding in the store for the identical reason
   * (see this module's header, KTD 1): the rep's unsaved conditions must
   * still be readable at decision time, which can be an arbitrary number of
   * renders — and potentially a navigation — after the gate opened, by which
   * point `SegmentationConditionsEditor`'s own `useState` draft may be gone.
   *
   * Optional rather than `| null` on every request because the vast majority
   * of pending requests are sequence saves and deletes that have no
   * conditions to carry; a caller may still pass an explicit `null`
   * (structurally identical to omitting it — both mean "no conditions here").
   */
  draftConditions?: SegmentationConditions | null;
  /**
   * Plan 2026-09-28-006 U3b (rulebook R6) — the settings-edit counterpart of
   * `draftSteps`/`draftConditions`, replacing round-6's sender-only
   * `draftSender`: {@link SequenceSettingsDraft}'s own doc has the full
   * rationale. Reuses `target.kind: 'sequence'` (Decision 7 of the round-6
   * plan still holds) — every settings edit is, at the store's level, "a
   * pending write against a sequence identity"; the PRESENCE of this field
   * (not its contents) is what distinguishes a settings pending from a steps
   * pending, exactly as `draftConditions` already distinguishes a
   * segmentation pending.
   */
  draftSettings?: SequenceSettingsDraft;
  /** `null` in delete mode. */
  headVersionId: string | null;
  places: PlaybooksUsagePlace[];
  /**
   * Plan 2026-09-28-006 U3b (rulebook R8, edge (d)) — the non-archived plays
   * whose own ladder already points at their own copy rooted in this target
   * (`PlaybooksUsageResponse.ownVersionPlays`; hosts pass the fetched usage
   * response's field straight through at open time). Option ② never touches
   * them; `saveMenuOptions` names how many in its description ("N plays
   * already have their own version and won't change"). `[]` for a delete
   * pending and for any target with no such plays — never omitted, so a
   * caller can never forget it the way an `.optional()` field invites.
   */
  ownVersionPlays: PlaybooksUsagePlay[];
  /**
   * Plan 2026-09-28-007 U9 (rulebook R9, edge (g)) — every non-archived
   * playbook whose CURRENT head ladder references this target TODAY (the
   * usage response's own `templatePlaybooks`, hosts pass it straight through
   * at open time — never re-derived from `places`, which can also carry a
   * PLAY-only place whose template has since moved the rung elsewhere, see
   * `PlaybooksTemplatePlaybookSchema`'s own doc). Replaces the old bare
   * count-only field, which could not tell ③ WHICH playbook(s) to
   * name (Codex b1-6). `saveMenuOptions` reads this to relabel ③ "Update
   * plays and the Library" (no playbook to name) when it is `[]` — a real,
   * common state (a piece used only by plays, never a playbook), not an
   * error. `[]` for an owned target and for a delete pending, where ③ never
   * shows anyway.
   */
  templatePlaybooks: PlaybooksTemplatePlaybook[];
  acknowledgedCount: number;
  /**
   * Round-4 QA fixes, U5 (S1) / Decision 6 — the unsent-play counterpart of
   * `acknowledgedCount`, same "frozen at open time, informational" contract:
   * the actual write always sends a FRESH `distinctUnsentPlays(pending.places)`
   * recompute (mirroring how the write already sends fresh
   * `pending.places.length` rather than this field's sibling — see
   * `handleUsageSidebarUpdateAll`'s own doc in `SequencesStep.tsx`).
   * Optional: `PlaybooksConfigPage.tsx`'s delete-mode `openUsageSidebar` call
   * (a different target kind, a different gate entirely) has no unsent-play
   * count to report and is not required to supply one.
   */
  acknowledgedUnsentPlayCount?: number;
  /**
   * U7a — the "Generate with AI" provenance for the save this sidebar is
   * gating, when the save came from AI Apply (U7b). `undefined` for every
   * save that never went through generation (the vast majority); `null` is
   * a legal, distinct "explicitly no generation" value a caller may pass.
   * `openUsageSidebar` copies this through unchanged via its `...input`
   * spread — see that function's doc.
   */
  generationInputs?: PlaybooksGenerationInputs | null;
  /**
   * Codex fixes 2026-09-17, F2 — the `'ready'` staged review the AI-Apply
   * save was pressed from, snapshotted by the host's `saveWithGate` at open
   * time. `PlayFlowShell`'s context-change clear (`clearGeneratedCopyReviews`)
   * legitimately wipes the live entry when the rep follows a card's "Go to
   * this play" to another flow; this copy is what `resumeReviewFromPending`
   * puts back (only if the live entry is gone — a live entry is newer, the
   * rep may have Accepted/Rejected while the sidebar was open). `undefined`
   * for every non-AI save. NOT the source of truth for the write —
   * `draftSteps` is; this exists only so "Keep reviewing" can honour its
   * name after an excursion.
   */
  stagedReview?: GeneratedCopyReview;
  decision: UsageSidebarDecision;
  /**
   * D2 — an immutable identity minted once per {@link openUsageSidebar} call
   * (`crypto.randomUUID()`), carried unchanged through the whole
   * decide→write→continue lifecycle. `use-usage-sidebar-decision.ts`'s async
   * continuation (close/refresh/toast) compares this against
   * `getState().pending?.requestId` before acting, so a stale continuation
   * from an EARLIER request can never act on a LATER one that has since
   * replaced it via `openUsageSidebar`'s unconditional-replace semantics.
   */
  requestId: string;
  /**
   * D2 — `'running'` from the moment {@link chooseUsageSidebarAction} records
   * a legal decision until the write settles (success, terminal failure, or
   * a conflict retry reopening for another pick). `UsageSidebar.tsx` disables
   * its footer buttons and short-circuits the close X while `running`.
   */
  status: 'idle' | 'running';
  /**
   * D17 (Codex NEW-RUNG-DELETED-WHILE-PENDING) — set by
   * {@link markUsageSidebarOriginMissing} when the origin this request was
   * opened for (a play's rung, resolved against the play's own live rail;
   * a library row, resolved against the library's own live row list) no
   * longer exists at the moment a decision is about to execute. `false` for
   * the entire lifetime of an ordinary request. Once `true`, `status` is
   * back to `'idle'` but the request is otherwise dead: `UsageSidebar.tsx`
   * swaps its footer for the removal notice and only the close X remains
   * usable — there is nothing left to save the decision against.
   */
  originMissing: boolean;
  /**
   * Plan 2026-09-28-008 U1 (rulebook R14) — the plain sentence a stale
   * refusal was answered with, or `null` while the request is live. Someone
   * else changed the group (or the piece) first: the save was refused and
   * NOTHING reloads on its own. The pending stays open holding the rep's
   * draft (`draftSteps`/`draftConditions`/`draftSettings` — the one holder a
   * closed popover or a reseeded editor cannot take away), `UsageSidebar.tsx`
   * shows this sentence with one "Reload to see their change" button, and
   * {@link chooseUsageSidebarAction} is a no-op (a retry would hit the same
   * refusal). Set by {@link markUsageSidebarStale} (a refused decision) or
   * passed to {@link openUsageSidebar} (a refusal before any menu was open);
   * cleared only by {@link retargetUsageSidebarPending} once the rep's Reload
   * (`usage-sidebar-reload.ts`) re-armed the request against the fresh piece.
   */
  staleRefusal: string | null;
}

/**
 * {@link openUsageSidebar}'s input — same shape as {@link PendingUsageSidebar}
 * minus `decision`/`requestId`/`status`/`originMissing`, which always start
 * fresh (`null`, a new uuid, `'idle'`, `false`). `staleRefusal` is optional
 * (default `null`): a host whose write was refused before any menu was open
 * opens the panel already stale with the same input the menu would have got.
 */
export type OpenUsageSidebarInput = Omit<
  PendingUsageSidebar,
  'decision' | 'requestId' | 'status' | 'originMissing' | 'staleRefusal'
> & { staleRefusal?: string | null };

/**
 * Round-4 QA codex-review fix (batch 1, finding 2) — U1's "auto-open Steps
 * after an apply-path save" only ever worked for the SOLE-USE branch of
 * `saveWithGate` (a plain `.then` on its own return). Once the save is
 * gated, the real write happens later inside `SequencesStep`'s play-level
 * `handleUsageSidebarUpdateAll`/`handleUsageSidebarSaveNew` — neither has a
 * props path back into the per-rung `SequenceEditorShell`'s local
 * `stepsOpen` state that requested the generation in the first place. This
 * tiny signal closes that gap: those two handlers call
 * {@link signalStepsReopen} with the rung's `draftKey` once their write
 * actually lands (and only when it was AI-apply-originated —
 * `pending.generationInputs` truthy, mirroring the sole-use branch's own
 * gate), and the shell for that SAME `draftKey` reopens Steps.
 *
 * `epoch`, not a bare boolean/string: a SECOND "Apply to sequence" for the
 * same rung, in a row, must still fire the shell's effect even though the
 * `rungDraftKey` value itself didn't change between the two signals — a
 * boolean would coalesce that into a no-op (React skips an effect whose
 * dependencies are `Object.is`-equal to last render).
 */
interface StepsReopenSignal {
  rungDraftKey: string;
  epoch: number;
}

interface UsageSidebarState {
  pending: PendingUsageSidebar | null;
  stepsReopenSignal: StepsReopenSignal | null;
}

/** Exported per the store convention — reset idiom is `useUsageSidebarStore.setState(initialState, true)`. */
export const initialState: UsageSidebarState = {
  pending: null,
  stepsReopenSignal: null,
};

export const useUsageSidebarStore = create<UsageSidebarState>(() => ({ ...initialState }));

export function selectStepsReopenSignal(state: UsageSidebarState): StepsReopenSignal | null {
  return state.stepsReopenSignal;
}

/** See {@link StepsReopenSignal}'s own doc for why this exists and why `epoch` monotonically increments rather than toggling a boolean. */
export function signalStepsReopen(rungDraftKey: string): void {
  useUsageSidebarStore.setState((state) => ({
    stepsReopenSignal: { rungDraftKey, epoch: (state.stepsReopenSignal?.epoch ?? 0) + 1 },
  }));
}

/**
 * Round-4 QA codex-review fix (wave 1, finding 6) — the missing consumer.
 * The signal was write-only: nothing ever cleared it, so it sat in module
 * scope for the rest of the session and re-fired `SequencesStep`'s reopen
 * effect every time that rung's shell remounted (a rail click to another
 * rung and back, a `?step=` tab round trip), forcing Steps open again long
 * after the save it was announcing.
 *
 * `epoch`-guarded rather than an unconditional `null`: a SECOND apply for the
 * same rung can land between the observing render and this call, and clearing
 * blindly would swallow that newer signal. A mismatch means someone else's
 * signal is current, so this consumer leaves it alone (the `{}` no-op
 * branch).
 */
export function consumeStepsReopenSignal(epoch: number): void {
  useUsageSidebarStore.setState((state) =>
    state.stepsReopenSignal?.epoch === epoch ? { stepsReopenSignal: null } : {},
  );
}

/**
 * The identity key {@link takeUsageSidebarDecision} matches against — one per
 * editing surface. A play origin keys off `rungDraftKey` (stable even before
 * the rung has a server `rungId`); a library origin keys off its target.
 */
export function originKeyOf(origin: UsageSidebarOrigin): string {
  // terra-qyt.13 — a `'playbook'` origin deliberately shares this SAME
  // `play:` prefix and shape with a `'play'` origin (not its own
  // `playbook:` prefix): both describe the identical editing surface (a
  // rung within a playbook), one place with two doorways. Only `'library'`
  // is a genuinely different surface.
  if (origin.kind === 'library') return `library:${origin.targetKind}:${origin.id}`;
  // D4 — `playbookId` disambiguates two DIFFERENT playbooks that each
  // happen to have staged an unsaved rung at the same `new:<n>` index
  // (`playbook-draft.ts`'s per-playbook staging counter starts over at
  // `new:1` for every playbook) — without it, two such playbooks would
  // collide on the exact same origin key.
  return `play:${origin.playbookId}:${origin.rungDraftKey}`;
}

export function selectUsageSidebar(state: UsageSidebarState): PendingUsageSidebar | null {
  return state.pending;
}

/**
 * Opens the sidebar for a new save/delete request, replacing any existing
 * pending request that is `'idle'` — the rapid-reclick case the old modal
 * guarded with `pendingGateRejectRef`. `decision` always starts `null`.
 *
 * D16 (Codex NEW-REQUEST-REPLACEMENT-DEADLOCK) — a request that is
 * `'running'` can NOT be replaced (no-op, plus an info toast). The old
 * unconditional-replace let a second `openUsageSidebar` call swap out the
 * pending object while the FIRST request's write was still in flight; that
 * first request's hook-mounted continuation (`use-usage-sidebar-decision.ts`)
 * then correctly refused to touch the now-foreign pending object once it
 * settled, but the SECOND request was left permanently `status: 'running'`
 * with its decision never taken by any hook (nothing re-drives it) —
 * footer disabled, close X inert, no way out short of a reload. Refusing
 * the replacement outright removes the window that bug needed: only one
 * request is ever in flight system-wide, so there is never a "later"
 * request for an "earlier" one's settling promise to strand.
 *
 * U6 (OQ4) — closes an open "Generate with AI" session first
 * (`generate-with-ai-store.ts`'s {@link closeGenerateSession}): the two
 * surfaces are mutually exclusive. This closes ONLY that drawer's session —
 * a staged generation in `generated-copy-store.ts` is untouched and survives
 * (R13), same as it survives this sidebar closing the other way.
 */
export function openUsageSidebar(input: OpenUsageSidebarInput): void {
  if (useUsageSidebarStore.getState().pending?.status === 'running') {
    addToast({ content: 'A save is still in progress — wait for it to finish.', status: 'info' });
    return;
  }
  closeGenerateSession();
  useUsageSidebarStore.setState({
    pending: {
      ...input,
      decision: null,
      requestId: crypto.randomUUID(),
      status: 'idle',
      originMissing: false,
      staleRefusal: input.staleRefusal ?? null,
    },
  });
}

/**
 * {@link openUsageSidebar}'s `'save'`-mode input minus the fields
 * {@link openUsageSidebarFromUsage} derives from the fresh usage response
 * itself (`places`, `ownVersionPlays`, `templatePlaybooks`,
 * `acknowledgedCount`, `acknowledgedUnsentPlayCount`) and minus `target`/
 * `origin`, which every host already has on hand before it ever fetches
 * usage. Derived with `Omit`, not hand-declared, so a new save-only field on
 * {@link PendingUsageSidebar} shows up here automatically.
 */
export type UsageSidebarSaveDraft = Omit<
  OpenUsageSidebarInput,
  | 'mode'
  | 'target'
  | 'origin'
  | 'places'
  | 'ownVersionPlays'
  | 'templatePlaybooks'
  | 'acknowledgedCount'
  | 'acknowledgedUnsentPlayCount'
>;

/**
 * Plan 2026-09-28-007 U5 (REVIEW-THESE follow-up) — builds a `'save'`
 * pending straight from a FRESH usage response, replacing the ~six
 * hand-copied field lists at every save-gate host (`SequencesStep.tsx`'s
 * `saveTemplateWithGate`/`savePlayWithGate`, `SequenceSenderPopover.tsx`,
 * `SegmentationConditionsEditor.tsx`, `SequenceCopyPane.tsx`): each used to
 * spell out `places`, `ownVersionPlays: usage.ownVersionPlays ?? []`,
 * `templatePlaybooks: owned ? [] : (usage.templatePlaybooks ?? [])`,
 * `acknowledgedCount: places.length` and
 * `acknowledgedUnsentPlayCount: distinctUnsentPlays(places)` by hand — the
 * class of drift U9 had to chase across five files when one call site
 * forgot a field. With one builder, a future field on
 * `PlaybooksUsageResponse` a pending needs can't be forgotten at one call
 * site because there is only one call site left.
 *
 * `templatePlaybooks` is copied straight from `usage`, with no caller-side
 * `owned ? [] : ...` guard: a playbook/template ladder only ever references
 * a Library id (rulebook R1), so the server's own `templatePlaybooks` is
 * already `[]` for an owned identity — the guard was defensive, never
 * load-bearing.
 */
export function openUsageSidebarFromUsage(
  usage: Pick<PlaybooksUsageResponse, 'places'> &
    Partial<Pick<PlaybooksUsageResponse, 'ownVersionPlays' | 'templatePlaybooks'>>,
  origin: UsageSidebarOrigin,
  target: UsageSidebarTarget,
  draft: UsageSidebarSaveDraft,
): void {
  const places = usage.places;
  openUsageSidebar({
    mode: 'save',
    target,
    origin,
    places,
    ownVersionPlays: usage.ownVersionPlays ?? [],
    templatePlaybooks: usage.templatePlaybooks ?? [],
    acknowledgedCount: places.length,
    acknowledgedUnsentPlayCount: distinctUnsentPlays(places),
    ...draft,
  });
}

/**
 * Drops the pending request — nothing about IT is written anywhere else.
 *
 * Deliberately NOT a full `setState(initialState, true)` reset (unlike test
 * teardown's own use of `initialState`, which DOES want a clean slate):
 * `handleUsageSidebarUpdateAll`/`handleUsageSidebarSaveNew` call
 * {@link signalStepsReopen} synchronously BEFORE their returning promise's
 * `.then` calls this function (`use-usage-sidebar-decision.ts`'s success
 * path) — a blind full reset here would wipe that signal in the same tick
 * it was set, before any subscriber ever observed the change.
 */
export function closeUsageSidebar(): void {
  useUsageSidebarStore.setState({ pending: null });
}

/**
 * terra-qyt round-3 fix U24 (W1 residual, Design decision 7) — drops a
 * pending usage-sidebar save that Apply parked for the playbook's PREVIOUS
 * seed campaign, when `PlayFlowShell#handleSeedChange` fires. Returns
 * whether it actually closed anything, so the caller can decide whether its
 * own info toast fired (it always does here, via {@link addToast}, so the
 * return value is informational/testable rather than load-bearing today).
 *
 * Three narrowings, each load-bearing:
 *  - **idle-only.** A `'running'` request's write is already in flight and
 *    was validated against the OLD seed at decision time (the
 *    `generationCampaignMismatch` check in `SequencesStep.tsx`'s two
 *    handlers ran before the write started) — there is nothing left to
 *    cancel, and closing the sidebar out from under it would only orphan
 *    its continuation. `use-usage-sidebar-decision.ts`'s own `isStillCurrent`
 *    request-id guard already makes any in-flight continuation a safe no-op
 *    if this were ever to race one anyway (belt-and-suspenders, not the
 *    reason for the guard).
 *  - **playbook-origin-only, matching playbookId.** A `'play'`/`'library'`
 *    pending belongs to a DIFFERENT editing surface entirely — a play's
 *    campaign is locked at creation and never changes out from under it,
 *    and a library save has no host campaign to move. Only a `'playbook'`
 *    origin's `seedCampaignSfdcId` can go stale this way, and only for the
 *    SAME playbook whose seed just changed.
 *  - **AI-provenance-only.** A hand-typed save or a delete carries no
 *    `generationInputs` (`undefined`/`null`) — that copy is campaign-neutral
 *    the same way a hand-typed copy draft is (`PlayFlowShell.tsx`'s own
 *    seed-change doc), so today's behavior (it survives) is correct and
 *    unchanged.
 */
export function closeUsageSidebarForSeedChange(playbookId: string): boolean {
  const { pending } = useUsageSidebarStore.getState();
  if (pending === null || pending.status !== 'idle') return false;
  if (pending.origin.kind !== 'playbook' || pending.origin.playbookId !== playbookId) return false;
  if (!pending.generationInputs) return false;
  closeUsageSidebar();
  addToast({
    content: 'Discarded the pending sequence save — its copy was generated for the previous seed campaign.',
    status: 'info',
  });
  return true;
}

/**
 * Records a rep's choice and flips `status` to `'running'` (D2) — the write
 * is about to start. A no-op when nothing is pending, when `decision` is
 * illegal for `pending.mode` (`'delete'` is only legal in `'delete'` mode,
 * `'update-all'`/`'save-new'` only in `'save'` mode), and — the double-click
 * guard — when `status` is ALREADY `'running'`: the footer buttons are
 * disabled at that point, but this store-level no-op is the real guarantee a
 * second click (or a second decision entirely) can never dispatch twice.
 *
 * `'resume-review'` (Codex fixes 2026-09-17, F2) is a save-mode decision like
 * the other two; it flips `status` to `'running'` for the one render until
 * the origin consumes it (the footer and X go inert, which is also what
 * stops a double-click).
 *
 * Plan 2026-09-28-007 U6 (rulebook R9, Codex c3-1) — `requestId` binds the
 * choice to the request it was made for: when given and it no longer matches
 * the CURRENT pending (a newer save replaced it while, say, the ③ confirm
 * was open), this is a silent no-op — no decision, no toast. `UsageSidebar`
 * always passes the id of the request its menu was rendered for; the
 * optional form exists for tests that drive the store directly.
 */
export function chooseUsageSidebarAction(decision: NonNullable<UsageSidebarDecision>, requestId?: string): void {
  useUsageSidebarStore.setState((state) => {
    if (state.pending === null) return state;
    if (requestId !== undefined && state.pending.requestId !== requestId) return state;
    if (state.pending.status === 'running') return state;
    // D17 — a dead request (its origin no longer exists) offers only the
    // close X; belt-and-suspenders against `UsageSidebar.tsx` rendering a
    // stale footer.
    if (state.pending.originMissing) return state;
    // Plan 2026-09-28-008 U1 (R14) — a stale pending offers only Reload and
    // the X; any choice would be refused again for the same reason.
    if (state.pending.staleRefusal !== null) return state;
    const legal =
      state.pending.mode === 'save' ? decision !== 'delete' : decision === 'delete';
    if (!legal) return state;
    return { pending: { ...state.pending, decision, status: 'running' } };
  });
}

/**
 * The one-shot decision handoff. Returns the full pending request and clears
 * its `decision` (back to `null`) ONLY when `originKeyOf(pending.origin)`
 * matches `originKey` AND a decision has actually been chosen — a
 * non-matching key returns `null` and leaves the decision untouched (a
 * different rung's `use-usage-sidebar-decision.ts` hook must never consume a
 * decision that isn't its own), and a second call with the SAME key returns
 * `null` because the decision was already cleared by the first call.
 */
export function takeUsageSidebarDecision(originKey: string): PendingUsageSidebar | null {
  const { pending } = useUsageSidebarStore.getState();
  if (pending === null) return null;
  if (originKeyOf(pending.origin) !== originKey) return null;
  if (pending.decision === null) return null;
  useUsageSidebarStore.setState({ pending: { ...pending, decision: null } });
  return pending;
}

/**
 * Plan 2026-09-28-008 U1 (rulebook R4, R8, R9; replaces the old
 * places-only refresh) — applies a FRESH usage read to the pending
 * request: `places`, `ownVersionPlays` and `templatePlaybooks` together, so
 * the Save menu's ② description and ③ confirm never read a refreshed list
 * beside a stale count or a stale playbook name (Codex round-3 F4). Two
 * callers: the `shared_mapping_conflict` retry (`use-usage-sidebar-decision.ts`)
 * and the rep's Reload (`usage-sidebar-reload.ts`). `templatePlaybooks` is
 * forced to `[]` for a play-owned target — the same rule hosts apply at open
 * (③ never shows for a copy). Same stale-requestId guard as
 * {@link resetUsageSidebarToIdle}.
 */
export function refreshUsageSidebarUsage(
  requestId: string,
  usage: Pick<PlaybooksUsageResponse, 'places' | 'ownVersionPlays' | 'templatePlaybooks'>,
): void {
  useUsageSidebarStore.setState((state) =>
    state.pending === null || state.pending.requestId !== requestId
      ? state
      : {
          pending: {
            ...state.pending,
            places: usage.places,
            ownVersionPlays: usage.ownVersionPlays,
            templatePlaybooks: state.pending.target.owningPlayId === null ? usage.templatePlaybooks : [],
          },
        },
  );
}

/**
 * Plan 2026-09-28-008 U1 (rulebook R14) — a decision (or a silent write) was
 * refused as stale: park the SAME request in the stale state — `status` back
 * to `'idle'`, `staleRefusal` set to the plain sentence — and change nothing
 * else. No refetch, no adopt, no reseed: the rep's draft stays exactly where
 * it is until they press Reload. Same stale-requestId guard as
 * {@link resetUsageSidebarToIdle}.
 */
export function markUsageSidebarStale(requestId: string, sentence: string): void {
  useUsageSidebarStore.setState((state) =>
    state.pending === null || state.pending.requestId !== requestId
      ? state
      : { pending: { ...state.pending, status: 'idle', staleRefusal: sentence } },
  );
}

/**
 * Plan 2026-09-28-008 U1 (rulebook R14) — the rep pressed "Reload to see
 * their change": flips a stale, idle request to `'running'` (Reload button
 * disabled, X inert) and reports whether it did. `false` — and no change —
 * for a replaced request, a request that is not stale, or one already
 * reloading (the double-click guard). `staleRefusal` is kept: only a
 * successful re-arm ({@link retargetUsageSidebarPending}) clears it.
 */
export function beginUsageSidebarReload(requestId: string): boolean {
  let began = false;
  useUsageSidebarStore.setState((state) => {
    const pending = state.pending;
    if (pending === null || pending.requestId !== requestId) return state;
    if (pending.staleRefusal === null || pending.status !== 'idle') return state;
    began = true;
    return { pending: { ...pending, status: 'running' } };
  });
  return began;
}

/**
 * D2 — reverts `status` back to `'idle'` (re-enabling the footer buttons and
 * the close X) WITHOUT touching `decision`/`places`/anything else. A no-op
 * unless the CURRENT pending request is still the one identified by
 * `requestId` — same stale-continuation guard `use-usage-sidebar-decision.ts`
 * applies before calling {@link closeUsageSidebar}/{@link refreshUsageSidebarUsage}
 * too: a request that has since been replaced by a fresh `openUsageSidebar`
 * call must never be resurrected by an earlier request's settling promise.
 */
export function resetUsageSidebarToIdle(requestId: string): void {
  useUsageSidebarStore.setState((state) =>
    state.pending === null || state.pending.requestId !== requestId
      ? state
      : { pending: { ...state.pending, status: 'idle' } },
  );
}

/**
 * Plan 2026-09-28-007 U6 (rulebook R7, Codex c1-3) — points the CURRENT
 * request at a different identity without minting a new request: keeps
 * `requestId`, `status`, `decision` and every draft field, swaps
 * `target.id`/`target.owningPlayId` and `places`.
 *
 * The one caller is a play's settings "Save as new — this group only": once
 * its fork has landed, the pending is retargeted onto the group's new copy
 * with that group alone as its place, BEFORE the settings write. If the write
 * then fails, the decision hook resets this same request to idle and
 * `saveMenuOptions` offers the single "Save" that writes onto the copy in
 * place — a retry never forks a second copy. Same stale-requestId guard as
 * {@link resetUsageSidebarToIdle}.
 *
 * Plan 2026-09-28-008 U1 (rulebook R14) — the second caller is the rep's
 * Reload (`usage-sidebar-reload.ts`), which re-arms a stale request against
 * the group's CURRENT piece: it may also swap `headVersionId` (the fresh
 * head a steps/conditions draft now saves against), `target.name` and
 * `target.archivedInLibrary`. Every retarget clears `staleRefusal` — the
 * request now names what the group really points at. Draft fields are never
 * touched.
 */
export function retargetUsageSidebarPending(
  requestId: string,
  next: {
    id: string;
    owningPlayId: string | null;
    places: PlaybooksUsagePlace[];
    headVersionId?: string | null;
    name?: string;
    archivedInLibrary?: boolean;
  },
): void {
  useUsageSidebarStore.setState((state) =>
    state.pending === null || state.pending.requestId !== requestId
      ? state
      : {
          pending: {
            ...state.pending,
            target: {
              ...state.pending.target,
              id: next.id,
              owningPlayId: next.owningPlayId,
              ...(next.name === undefined ? {} : { name: next.name }),
              ...(next.archivedInLibrary === undefined ? {} : { archivedInLibrary: next.archivedInLibrary }),
            },
            places: next.places,
            ...(next.headVersionId === undefined ? {} : { headVersionId: next.headVersionId }),
            staleRefusal: null,
          },
        },
  );
}

/**
 * D17 (Codex NEW-RUNG-DELETED-WHILE-PENDING) — the origin this request was
 * opened for (a play's rung / a library row) no longer exists at the moment
 * its decision is about to run. The caller (`SequencesStep.tsx`'s reselect
 * effect for a play origin) resolves that against its own live rail/row
 * list BEFORE calling this — this store has no view of either list itself.
 *
 * Reverts `status` to `'idle'` (there is no write in flight — the decision
 * was never taken, `takeUsageSidebarDecision` never ran) and sets
 * `originMissing: true`, which `UsageSidebar.tsx` renders as the removal
 * notice with only the close X usable. Same stale-requestId guard as
 * {@link resetUsageSidebarToIdle}: a no-op unless `requestId` still matches
 * the CURRENT pending request.
 */
export function markUsageSidebarOriginMissing(requestId: string): void {
  useUsageSidebarStore.setState((state) =>
    state.pending === null || state.pending.requestId !== requestId
      ? state
      : { pending: { ...state.pending, status: 'idle', originMissing: true } },
  );
}
